import ReactGA from "react-ga4";
import ConfigData from "../Config/config.json";
import { PLAN_TYPE } from "./constants";

/**
 * HTPTV-682 — GA4 `user_id` wiring.
 *
 * Sets GA4's `user_id` to our canonical platform UserID so GA behaviour can be
 * joined to viewership / subscription data. This is the SAME id that is:
 *   - stored as `UserID` on the auth object in localStorage ("auth"),
 *   - stamped server-side into `UserVideoAnalyse.UserID` (from the auth token),
 *   - passed to the players as the bitmovin `customUserId`.
 *
 * Mechanism note: the ticket suggested `gtag('config', MEASUREMENT_ID, { user_id })`,
 * but a *second* `config` call does NOT propagate user_id onto react-ga4's
 * event-based page_views — verified live in the network panel (`uid` was absent
 * on post-login hits). `gtag('set', { user_id })` sets it globally so EVERY
 * subsequent hit carries `uid` (verified: `uid=4` on `/g/collect`). PII
 * (email / first / last name) must NEVER be sent as the id.
 */

// GA is only initialised (App.tsx) for tenants that have a gTag in config.json.
const isGaConfiguredForTenant = (): boolean => {
  const websiteName = process.env.REACT_APP_ENV as string | undefined;
  return Boolean(
    (ConfigData as Record<string, { gTag?: string }>)?.[websiteName ?? ""]?.gTag
  );
};

const readUserIdFromStorage = (): string | number | undefined => {
  try {
    const raw = localStorage.getItem("auth");
    if (!raw) return undefined;
    return JSON.parse(raw)?.UserID;
  } catch {
    return undefined;
  }
};

/**
 * Attach our canonical UserID to GA4 via `gtag('set', { user_id })`.
 * No-ops safely when GA isn't configured for the tenant or no user is logged in.
 *
 * @param userId optional explicit id (e.g. straight from the login response);
 *               falls back to the `UserID` on the stored auth object.
 */
export const setGaUserId = (userId?: string | number | null): void => {
  const id = userId ?? readUserIdFromStorage();
  if (!isGaConfiguredForTenant()) return;
  if (id === undefined || id === null || id === "") return;
  // String for a stable, non-PII identifier; applies to all subsequent hits.
  ReactGA.gtag("set", { user_id: String(id) });
};

/**
 * Clear the GA4 `user_id` (call on logout so a shared browser does not carry the
 * previous user's id into the next, pre-login session).
 */
export const clearGaUserId = (): void => {
  if (!isGaConfiguredForTenant()) return;
  ReactGA.gtag("set", { user_id: null });
};

/**
 * HTPTV-683 — persistent GA4 user properties via `gtag('set', 'user_properties', {...})`.
 * No PII. Drops undefined/empty values; no-ops when GA isn't configured for the
 * tenant or nothing is left to set.
 */
export const setGaUserProperties = (
  props: Record<string, string | number | null | undefined>
): void => {
  if (!isGaConfiguredForTenant()) return;
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v !== undefined && v !== null && v !== "") clean[k] = String(v);
  }
  if (Object.keys(clean).length === 0) return;
  ReactGA.gtag("set", "user_properties", clean);
};

/**
 * Derive subscription user-properties from the `get-user-subscription-details`
 * `data` payload (HTPTV-683).
 *
 * `subscription_status` is the 3-state subscriber/expired/none:
 *   - subscriber: Stripe `subscription.status` is `active` | `trialing`
 *   - expired:    a subscription record exists but is no longer active
 *   - none:       no subscription at all (incl. single-game-only buyers)
 *
 * `plan_tier` / `billing_period` are only set for active subscribers:
 *   - plan_tier:     data.plan_type_id — current plan (2 = insider, else basic);
 *                    NOT subscription.metadata.PlanTypeID, which goes stale on upgrade
 *   - billing_period: subscription.plan.interval (year -> annual, month -> monthly)
 */
export const deriveSubscriptionProperties = (
  data: any
): Record<string, string> => {
  const sub = data?.subscription;
  const status = sub?.status;
  const subscription_status =
    status === "active" || status === "trialing"
      ? "subscriber"
      : sub
      ? "expired"
      : "none";
  const props: Record<string, string> = { subscription_status };
  if (subscription_status === "subscriber") {
    // Prefer the backend's recomputed top-level plan_type_id: Stripe's
    // subscription.metadata.PlanTypeID goes STALE after a plan change/upgrade
    // (it keeps the original plan's id), whereas data.plan_type_id reflects the
    // CURRENT plan and matches the entitlement check. Fall back to metadata.
    const planTypeId = Number(data?.plan_type_id ?? sub?.metadata?.PlanTypeID);
    if (planTypeId === PLAN_TYPE.INSIDER) props.plan_tier = "insider";
    else if (planTypeId === PLAN_TYPE.BASIC) props.plan_tier = "basic";
    const interval = sub?.plan?.interval; // 'month' | 'year'
    props.billing_period = interval === "year" ? "annual" : "monthly";
  }
  return props;
};

/**
 * HTPTV-684 — fire a GA4 event (signup funnel: sign_up_start / sign_up / login).
 * snake_case event names, identical across tenants; params must contain no PII.
 * No-ops when GA isn't configured for the tenant.
 */
export const trackGaEvent = (
  name: string,
  params: Record<string, string | number> = {}
): void => {
  if (!isGaConfiguredForTenant()) return;
  ReactGA.gtag("event", name, params);
};

/** Map a plan's PlanTypeID to a GA `plan_tier` (HTPTV-685/683). */
export const planTierFromId = (planTypeId: unknown): string | undefined => {
  const id = Number(planTypeId);
  if (id === PLAN_TYPE.INSIDER) return "insider";
  if (id === PLAN_TYPE.BASIC) return "basic";
  return undefined;
};

/**
 * HTPTV-685 — map a plan-details object (from GetPlanDetailsById /
 * get-user-subscription-details) to GA4 e-commerce params. Stripe amounts are in
 * minor units (cents), so `value` is converted to major units. No PII.
 */
export const planToGaParams = (plan: any): Record<string, string | number> => {
  const out: Record<string, string | number> = {};
  const amount = Number(
    plan?.price_in_currency ?? plan?.amount ?? plan?.plan?.amount
  );
  if (Number.isFinite(amount)) out.value = Math.round(amount) / 100;
  const currency = plan?.currency ?? plan?.plan?.currency;
  if (currency) out.currency = String(currency).toUpperCase();
  const planId = plan?.plan_id ?? plan?.PlanID ?? plan?.plan?.id;
  if (planId !== undefined && planId !== null && planId !== "")
    out.plan_id = String(planId);
  const tier = planTierFromId(plan?.plan_type_id ?? plan?.PlanTypeID);
  if (tier) out.plan_tier = tier;
  const interval = plan?.interval ?? plan?.plan?.interval;
  const name = String(plan?.name ?? "").toLowerCase();
  const billing =
    interval === "year" || name.includes("year") || name.includes("annual")
      ? "annual"
      : interval === "month" || name.includes("month")
      ? "monthly"
      : undefined;
  if (billing) out.billing_period = billing;
  return out;
};

// HTPTV-685 (phase 2) — purchase fires on the post-payment landing page (Stripe
// redirects away on success), so the params are stashed before the redirect.
const PENDING_PURCHASE_KEY = "ga_pending_purchase";
const PENDING_GAME_ID_KEY = "ga_pending_game_id";

/**
 * Build the GA4 `purchase` payload (everything except transaction_id, which the
 * payment provider returns on redirect). `product_type` distinguishes a
 * subscription from a single-game (pay-as-you-go) purchase; a single_game
 * purchase carries game_id and must NOT alter subscription_status.
 */
export const buildPendingPurchase = (
  plan: any,
  gameId?: string | null
): Record<string, string | number> => {
  const p = planToGaParams(plan);
  const isSingleGame =
    Number(plan?.plan_type_id ?? plan?.PlanTypeID) === PLAN_TYPE.PAY_AS_YOU_GO;
  const out: Record<string, string | number> = {
    product_type: isSingleGame ? "single_game" : "subscription",
  };
  if (p.value !== undefined) out.value = p.value;
  if (p.currency) out.currency = p.currency;
  if (isSingleGame) {
    if (gameId) out.game_id = String(gameId);
  } else {
    if (p.plan_id) out.plan_id = p.plan_id;
    if (p.plan_tier) out.plan_tier = p.plan_tier;
    if (p.billing_period) out.billing_period = p.billing_period;
  }
  return out;
};

/** Stash the pending purchase just before Stripe redirects away. */
export const stashPendingPurchase = (
  purchase: Record<string, string | number>
): void => {
  try {
    sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(purchase));
  } catch {}
};

/** Remember the game being bought (set when single-game checkout starts). */
export const stashPendingGameId = (gameId: string | number): void => {
  try {
    if (gameId) sessionStorage.setItem(PENDING_GAME_ID_KEY, String(gameId));
  } catch {}
};

export const readPendingGameId = (): string | null => {
  try {
    return sessionStorage.getItem(PENDING_GAME_ID_KEY);
  } catch {
    return null;
  }
};

/**
 * On the post-payment landing page, fire GA4 `purchase` once Stripe has
 * redirected back with redirect_status=succeeded. transaction_id is the Stripe
 * payment_intent; the rest comes from the pre-redirect stash. Clears the stash
 * so it fires at most once per purchase.
 */
export const trackPurchaseFromRedirect = (params: URLSearchParams): void => {
  if (params.get("redirect_status") !== "succeeded") return;
  const transactionId = params.get("payment_intent");
  if (!transactionId) return;
  let pending: Record<string, string | number> | null = null;
  try {
    const raw = sessionStorage.getItem(PENDING_PURCHASE_KEY);
    if (raw) pending = JSON.parse(raw);
  } catch {}
  if (!pending) return; // no stashed context -> don't fire a malformed purchase
  try {
    sessionStorage.removeItem(PENDING_PURCHASE_KEY);
    sessionStorage.removeItem(PENDING_GAME_ID_KEY);
  } catch {}
  trackGaEvent("purchase", { transaction_id: transactionId, ...pending });
};

// HTPTV-687 — on-platform search. Single event `view_search_results`, split by a
// fixed `search_context` enum (keep this list in sync with the measurement plan).
export const SEARCH_CONTEXT = {
  GLOBAL_NAV: "global_nav",
  VOD_LIBRARY: "vod_library",
  SCHEDULE: "schedule",
  TEAM_PAGE: "team_page",
  SHORT_CONTENT: "short_content",
  IN_GAME: "in_game",
} as const;

/**
 * Fire `view_search_results` for an on-platform search. Zero-result searches are
 * captured (pass results_count: 0). No PII. Blank terms are skipped.
 */
export const trackSearch = (params: {
  search_term: string;
  search_context: string;
  results_count?: number;
}): void => {
  const term = (params.search_term ?? "").trim();
  if (!term) return;
  const out: Record<string, string | number> = {
    search_term: term,
    search_context: params.search_context,
    tenant: (process.env.REACT_APP_ENV as string) || "",
  };
  if (
    params.results_count !== undefined &&
    params.results_count !== null &&
    Number.isFinite(params.results_count)
  )
    out.results_count = params.results_count;
  trackGaEvent("view_search_results", out);
};
